"use client";

import { useState } from 'react';
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from '@/utils/supabase/client';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Send } from 'lucide-react';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from 'react-hook-form';
import * as z from "zod";
import { showSuccess, showError } from '@/utils/toast';

const schema = z.object({
  content: z.string().min(1, 'Comment cannot be empty'),
});

type FormData = z.infer<typeof schema>;

interface TaskCommentFormProps {
  taskId: string;
  parentId?: string; // For replies
  onCommentAdded?: () => void;
  placeholder?: string;
}

export const TaskCommentForm: React.FC<TaskCommentFormProps> = ({ taskId, parentId, onCommentAdded, placeholder }) => {
  const { profile } = useAuth();
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      content: '',
    },
  });

  const handleSubmit = async (data: FormData) => {
    if (!profile?.id) return;

    try {
      const { error } = await supabase
        .from('task_comments')
        .insert({
          task_id: taskId,
          user_id: profile.id,
          content: data.content,
          parent_id: parentId || null,
          company_id: profile.company_id,
        });

      if (error) throw error;

      showSuccess('Comment added!');
      form.reset();
      onCommentAdded?.();
    } catch (error: any) {
      showError('Failed to add comment');
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-2">
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  placeholder={placeholder || "Add a comment..."}
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" className="w-full">
          <Send className="mr-2 h-4 w-4" />
          Post Comment
        </Button>
      </form>
    </Form>
  );
};